import { useMemo } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { Alert, Box, Typography } from "@mui/material";
import { Timestamp, collection, getDocs, query, where } from "firebase/firestore";
import {
  ADMIN_REFETCH_INTERVAL,
  getFirebaseClient,
  useAuth,
  StatCard,
} from "@workspace/admin-ui";
import { PageHeader } from "@/components/AppShell";
import { pharmacyAdminApi, pharmacyAdminBookingsApi } from "@/lib/callables";

function dateRanges() {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekStart = new Date(todayStart);
  weekStart.setDate(todayStart.getDate() - todayStart.getDay());
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const ninetyDaysOut = new Date(todayStart);
  ninetyDaysOut.setDate(todayStart.getDate() + 90);
  return {
    todayMs: todayStart.getTime(),
    weekMs: weekStart.getTime(),
    monthStart,
    monthStartIso: monthStart.toISOString(),
    monthStartStr: monthStart.toISOString().split("T")[0],
    todayStr: todayStart.toISOString().split("T")[0],
    ninetyDaysOutStr: ninetyDaysOut.toISOString().split("T")[0],
  };
}

/**
 * `consultations/{id}.createdAt` is an ISO-8601 string, not a Firestore
 * Timestamp — the mobile app writes the consultation's own `createdAt` field
 * verbatim (see artifacts/ccs-pharmacy/src/services/firebase/consultationSync.ts).
 * Records written by an older client may still hold a Timestamp, so read both
 * rather than assuming either.
 */
function createdAtMillis(value: unknown): number | null {
  if (typeof value === "string") {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof (value as { toMillis?: () => number } | null)?.toMillis === "function") {
    return (value as { toMillis: () => number }).toMillis();
  }
  return null;
}

function isThisWeek(dateStr: string, todayStr: string): boolean {
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date(todayStr + "T00:00:00");
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay());
  return d >= weekStart && d <= today;
}

export default function DashboardPage() {
  const { membership } = useAuth();
  const chainId = membership?.chainId ?? "";
  const pharmacyId = membership?.pharmacyId ?? "";
  const { db } = getFirebaseClient();
  const { todayMs, weekMs, monthStart, monthStartIso, monthStartStr, todayStr, ninetyDaysOutStr } =
    useMemo(dateRanges, []);

  const pharmacistsQ = useQuery({
    queryKey: ["pharmacy-admin", "members", "all"],
    queryFn: () => pharmacyAdminApi.listPharmacists({ status: "all" }),
    enabled: !!pharmacyId,
    refetchInterval: ADMIN_REFETCH_INTERVAL,
  });

  const consultationsQ = useQuery({
    queryKey: ["pharmacy-admin", "consultations", "dashboard", monthStartIso],
    queryFn: async () => {
      // Firestore orders values by type before it compares them, so one range
      // bound can only ever match one of the two shapes `createdAt` has been
      // written in: an ISO string never matches a Timestamp bound and vice
      // versa. Ask for both shapes and merge, or every record written by the
      // other client silently drops out of the figures.
      const scope = [
        where("chainId", "==", chainId),
        where("pharmacyId", "==", pharmacyId),
      ];
      const [isoSnap, timestampSnap] = await Promise.all([
        getDocs(
          query(collection(db, "consultations"), ...scope, where("createdAt", ">=", monthStartIso)),
        ),
        getDocs(
          query(
            collection(db, "consultations"),
            ...scope,
            where("createdAt", ">=", Timestamp.fromDate(monthStart)),
          ),
        ),
      ]);
      // A record matching both bounds must only be counted once.
      const createdAtById = new Map<string, number | null>();
      for (const d of [...isoSnap.docs, ...timestampSnap.docs]) {
        createdAtById.set(d.id, createdAtMillis((d.data() as Record<string, unknown>).createdAt));
      }
      return [...createdAtById.values()].map((createdAtMs) => ({ createdAtMs }));
    },
    enabled: !!chainId && !!pharmacyId,
    refetchInterval: ADMIN_REFETCH_INTERVAL,
  });

  const bookingPharmaciesQ = useQuery({
    queryKey: ["bookings", "pharmacies"],
    queryFn: () => pharmacyAdminBookingsApi.listBookingPharmacies(),
    enabled: !!pharmacyId,
    refetchInterval: ADMIN_REFETCH_INTERVAL,
  });

  const bookingPharmacies = bookingPharmaciesQ.data?.items ?? [];

  const bookingQueries = useQueries({
    queries: bookingPharmacies.map((p) => ({
      queryKey: ["pharmacy-admin", "bookings", p.id, "dashboard", monthStartStr],
      queryFn: () =>
        pharmacyAdminBookingsApi.listBookings({
          bookingPharmacyId: p.id,
          startDate: monthStartStr,
          endDate: todayStr,
        }),
      enabled: !!pharmacyId,
      refetchInterval: ADMIN_REFETCH_INTERVAL,
    })),
  });

  const upcomingBookingQueries = useQueries({
    queries: bookingPharmacies.map((p) => ({
      queryKey: ["pharmacy-admin", "bookings", p.id, "upcoming", todayStr],
      queryFn: () =>
        pharmacyAdminBookingsApi.listBookings({
          bookingPharmacyId: p.id,
          startDate: todayStr,
          endDate: ninetyDaysOutStr,
        }),
      enabled: !!pharmacyId,
      refetchInterval: ADMIN_REFETCH_INTERVAL,
    })),
  });

  const pharmacists = pharmacistsQ.data?.items ?? [];
  const activePharmacists = pharmacists.filter((m) => m.status === "active").length;
  const suspendedPharmacists = pharmacists.filter((m) => m.status === "suspended").length;

  const consultations = consultationsQ.data ?? [];
  const consultationsMonth = consultations.length;
  const consultationsWeek = consultations.filter(
    (c) => c.createdAtMs !== null && c.createdAtMs >= weekMs,
  ).length;
  const consultationsToday = consultations.filter(
    (c) => c.createdAtMs !== null && c.createdAtMs >= todayMs,
  ).length;

  const allBookings = bookingQueries.flatMap((q) => q.data?.items ?? []);
  const bookingsLoading =
    bookingPharmaciesQ.isLoading || bookingQueries.some((q) => q.isLoading);
  const bookingsMonth = allBookings.length;
  const bookingsWeek = allBookings.filter((b) => isThisWeek(b.date, todayStr)).length;
  const bookingsToday = allBookings.filter((b) => b.date === todayStr).length;

  const upcomingBookings = upcomingBookingQueries.flatMap((q) => q.data?.items ?? []).length;
  const upcomingLoading =
    bookingPharmaciesQ.isLoading || upcomingBookingQueries.some((q) => q.isLoading);

  // Every figure on this page defaults to zero while its source is missing,
  // so a failed read would otherwise be indistinguishable from a quiet
  // pharmacy. Say which read failed instead of letting a manager act on
  // numbers that were never loaded.
  const loadError = [
    pharmacistsQ.error,
    consultationsQ.error,
    bookingPharmaciesQ.error,
    ...bookingQueries.map((q) => q.error),
    ...upcomingBookingQueries.map((q) => q.error),
  ].find(Boolean);

  const lastUpdated = useMemo(() => {
    const timestamps = [
      pharmacistsQ.dataUpdatedAt,
      consultationsQ.dataUpdatedAt,
      bookingPharmaciesQ.dataUpdatedAt,
      ...bookingQueries.map((q) => q.dataUpdatedAt),
      ...upcomingBookingQueries.map((q) => q.dataUpdatedAt),
    ].filter(Boolean);
    return timestamps.length > 0 ? Math.max(...timestamps) : 0;
  }, [
    pharmacistsQ.dataUpdatedAt,
    consultationsQ.dataUpdatedAt,
    bookingPharmaciesQ.dataUpdatedAt,
    bookingQueries,
    upcomingBookingQueries,
  ]);

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="An overview of your pharmacy's activity"
      />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {lastUpdated > 0 && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", mb: 2, textAlign: "right" }}
          >
            Last updated {new Date(lastUpdated).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · refreshes every minute
          </Typography>
        )}

        {loadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Some figures could not be loaded, so the totals below are
            incomplete: {loadError instanceof Error ? loadError.message : String(loadError)}
          </Alert>
        )}

        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 2, textTransform: "uppercase", letterSpacing: 0.8, fontSize: "0.7rem" }}
        >
          Staff
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: 2,
            mb: 4,
          }}
        >
          <StatCard
            label="Active Pharmacists"
            value={activePharmacists}
            loading={pharmacistsQ.isLoading}
            subItems={[
              { label: "total", value: pharmacists.length },
              ...(suspendedPharmacists > 0
                ? [{ label: "suspended", value: suspendedPharmacists, color: "warning" as const }]
                : []),
            ]}
          />
          <StatCard
            label="Upcoming Bookings"
            value={upcomingLoading ? "—" : upcomingBookings}
            loading={upcomingLoading}
            description="Next 90 days"
          />
        </Box>

        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 2, textTransform: "uppercase", letterSpacing: 0.8, fontSize: "0.7rem" }}
        >
          Activity this month
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
            gap: 2,
          }}
        >
          <StatCard
            label="Bookings — Today"
            value={bookingsLoading ? "—" : bookingsToday}
            loading={bookingsLoading}
          />
          <StatCard
            label="Bookings — This Week"
            value={bookingsLoading ? "—" : bookingsWeek}
            loading={bookingsLoading}
          />
          <StatCard
            label="Bookings — This Month"
            value={bookingsLoading ? "—" : bookingsMonth}
            loading={bookingsLoading}
          />
          <StatCard
            label="Consultations — Today"
            value={consultationsQ.isLoading ? "—" : consultationsToday}
            loading={consultationsQ.isLoading}
          />
          <StatCard
            label="Consultations — This Week"
            value={consultationsQ.isLoading ? "—" : consultationsWeek}
            loading={consultationsQ.isLoading}
          />
          <StatCard
            label="Consultations — This Month"
            value={consultationsQ.isLoading ? "—" : consultationsMonth}
            loading={consultationsQ.isLoading}
          />
        </Box>
      </Box>
    </>
  );
}
